import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import ConversationList, {
  Conversation,
} from '@/components/Chat/ConversationList'
import ChatSessionComponent from '@/components/Chat/ChatSession'
import { useChatStore } from '@/stores/chatStore'
import { useSessionCache } from '@/hooks/useSessionCache'
import {
  useAgentDetail,
  useAgentDetailWithSearchConfig,
} from '@/api/hooks/useAgent'
import type { ChatSession as ChatSessionType } from '@/api/types/chat.type'

// 将API返回的ChatSession转换为组件需要的Conversation格式
const convertSessionToConversation = (
  session: ChatSessionType
): Conversation => ({
  id: session.sessionId,
  title: session.title,
  lastMessage: '', // 这里可以后续从消息记录中获取
  timestamp: new Date(session.updatedAt),
})

/**
 * 聊天会话页面组件
 * 专门处理具体的会话页面，包含侧边栏和会话内容
 */
const ChatSessionPage = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const sessionCache = useSessionCache()
  const { sessions, removeSession, updateSession } = useChatStore()

  // 从 URL 查询参数中获取智能体ID
  const urlParams = new URLSearchParams(window.location.search)
  const agentId = urlParams.get('id')

  // 获取智能体详情
  const { data: agentDetail } = useAgentDetail(agentId || undefined)

  // 获取智能体知识库配置
  const { searchConfig } = useAgentDetailWithSearchConfig(agentDetail)

  // 存储新建会话时的初始消息
  const [initialMessages, setInitialMessages] = useState<Map<string, string>>(
    new Map()
  )

  // 从sessionStorage获取初始消息
  useEffect(() => {
    if (sessionId) {
      const storedMessage = sessionStorage.getItem(
        `initial_message_${sessionId}`
      )
      if (storedMessage) {
        console.log('Found initial message in sessionStorage:', {
          sessionId,
          storedMessage,
        })
        setInitialMessages((prev) =>
          new Map(prev).set(sessionId, storedMessage)
        )
        // 立即清除sessionStorage中的消息，避免重复使用
        sessionStorage.removeItem(`initial_message_${sessionId}`)
      } else {
        console.log('No initial message found for sessionId:', sessionId)
      }
    }
  }, [sessionId])

  // 转换会话列表格式
  const conversations = useMemo(
    () => sessions.map(convertSessionToConversation),
    [sessions]
  )

  // 定期清理过期缓存
  useEffect(() => {
    const interval = setInterval(() => {
      sessionCache.clearExpiredCache()
    }, 5 * 60 * 1000) // 每5分钟清理一次

    return () => clearInterval(interval)
  }, [sessionCache])

  // 处理会话选择
  const handleSelectConversation = (conversationId: string) => {
    console.log('ChatSessionPage handleSelectConversation called:', {
      conversationId,
      currentSessionId: sessionId,
      willNavigate: conversationId !== sessionId,
    })

    // 根据是否有agentId判断是智能体聊天还是普通聊天
    const isAgentChat = !!agentId
    console.log('isAgentChat', isAgentChat, agentId)
    const targetPath =
      isAgentChat && agentId
        ? `/agents/chat/s/${conversationId}?id=${agentId}`
        : `/chat/s/${conversationId}`

    navigate(targetPath)
  }

  // 创建新对话
  const handleNewConversation = async () => {
    console.log('handleNewConversation called')

    // 根据是否有agentId判断是智能体聊天还是普通聊天
    const isAgentChat = !!agentId
    const targetPath =
      isAgentChat && agentId ? `/agents/chat/${agentId}` : '/chat'

    navigate(targetPath)
  }

  // 删除对话
  const handleDeleteConversation = async (conversationId: string) => {
    try {
      // 从store中删除会话
      removeSession(conversationId)

      // 清除被删除会话的缓存
      sessionCache.clearSessionCache(conversationId)

      // 如果删除的是当前对话，切换到第一个对话或欢迎页面
      if (conversationId === sessionId) {
        const remainingSessions = sessions.filter(
          (s) => s.sessionId !== conversationId
        )

        // 根据是否有agentId判断是智能体聊天还是普通聊天
        const isAgentChat = !!agentId

        if (remainingSessions.length > 0) {
          const newSessionId = remainingSessions[0].sessionId
          console.log('Navigating to new session after delete:', newSessionId)
          const targetPath =
            isAgentChat && agentId
              ? `/agents/chat/s/${newSessionId}?id=${agentId}`
              : `/chat/s/${newSessionId}`
          navigate(targetPath)
        } else {
          console.log('Navigating to welcome page after delete')
          const targetPath =
            isAgentChat && agentId ? `/agents/chat/${agentId}` : '/chat'
          navigate(targetPath)
        }
      }
    } catch (error) {
      console.error('删除对话失败:', error)
    }
  }

  // 重命名对话
  const handleRenameConversation = async (
    conversationId: string,
    newTitle: string
  ) => {
    try {
      // 更新store中的会话
      updateSession(conversationId, newTitle)

      console.log('重命名对话:', conversationId, '新标题:', newTitle)
    } catch (error) {
      console.error('重命名对话失败:', error)
    }
  }

  // 如果sessionId不存在，重定向到欢迎页面
  if (!sessionId) {
    navigate('/chat')
    return null
  }

  return (
    <div className="flex h-full">
      {/* 侧边栏 - 对话列表 */}
      <ConversationList
        conversations={conversations}
        activeConversationId={sessionId}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        onDeleteConversation={handleDeleteConversation}
        onRenameConversation={handleRenameConversation}
      />

      {/* 主内容区 */}
      <div className="flex-1 flex flex-col h-full">
        <ChatSessionComponent
          sessionId={sessionId}
          isActive={true}
          initialMessage={initialMessages.get(sessionId)}
          agentId={agentId || undefined}
          agentDetail={agentDetail}
          searchConfig={searchConfig}
        />
      </div>
    </div>
  )
}

export default ChatSessionPage
